/**
 * Flota — leasingi i ustawienia floty (`fleet.web.leasesUi.*`, `fleet.web.settingsUi.*`), doklejone w `extra.ts`.
 * `fleetLeasesUiEn` jest typowany względem `fleetLeasesUiPl` — brak klucza = błąd kompilacji.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const fleetLeasesUiPl = {
  leasesUi: {
    rateUnknown: 'stawka nieznana',
    hero: { limit: 'Limit', rate: 'Stawka', rateNone: 'nieznana', ofLimit: 'limitu' },
    kpi: {
      exceeded: 'Przekroczono o',
      avgHint: 'z {days} dni umowy',
      costHint: 'przy stawce {rate}',
    },
    card: {
      forecast: 'Prognoza: {percent}% limitu',
      cost: 'koszt przekroczenia {cost}',
      daysLeft: 'zostało {days} dni',
    },
    summary: {
      active: 'Aktywne umowy',
      activeHint: 'Wszystkie aktywne, bez względu na filtry',
      truncated: 'Pierwsze {n} aktywnych umów',
      atRisk: 'W ryzyku',
      atRiskHint: 'Ostrzeżenie lub prognoza ponad limit',
      exceeded: 'Limit przekroczony',
      exceededHint: 'Przejechano więcej niż limit umowy',
      cost: 'Prognozowany koszt przekroczeń',
      costHint: 'Suma tylko dla umów ze znaną stawką',
      costNoRate: 'Pominięto umowy bez stawki: {n}',
      costNone: 'Brak prognozowanych przekroczeń z wyliczalnym kosztem',
    },
    chart: {
      title: 'Przebieg a ścieżka limitu',
      description: 'Narastające kilometry z odczytów licznika, równomierna ścieżka limitu i prognoza do końca umowy.',
      aria: 'Wykres narastającego przebiegu umowy: przejechano {driven} z limitu {limit}, prognoza na koniec umowy: {forecast}',
      noForecastShort: 'brak',
      limitLabel: 'Limit {km}',
      today: 'Dziś',
      pathAt: 'Ścieżka limitu: {km}',
      ahead: 'Przed ścieżką o {km}',
      behind: 'Za ścieżką o {km}',
      legendActual: 'Przejechane km',
      legendPath: 'Ścieżka limitu (równomiernie)',
      legendForecast: 'Prognoza',
      legendForecastIndicative: 'Prognoza orientacyjna',
      legendZone: 'Strefa przekroczenia',
      indicativeNote: 'Prognoza jest orientacyjna — za mało danych (krótki okres lub niski przebieg).',
      empty: 'Za mało odczytów licznika, by narysować wykres',
      emptyHint: 'Wykres pojawi się po co najmniej jednym odczycie licznika w okresie umowy.',
      tableToggle: 'Pokaż dane jako tabelę',
      colDate: 'Data',
      colKm: 'Przejechane',
      colPercent: '% limitu',
    },
    sim: {
      title: 'Symulator „co jeśli”',
      description: 'Zmień średnie tempo jazdy i zobacz przewidywane przekroczenie. To szacunek, nie obietnica.',
      pace: 'Średnio do końca umowy',
      perMonth: 'km / mies.',
      useCurrent: 'Obecne tempo ({km})',
      useFit: 'Mieszczące się w limicie ({km})',
      resultTitle: 'Przewidywany przebieg na koniec umowy',
      overage: 'Przekroczenie',
      ended: 'Umowa się zakończyła — symulacja nie ma już sensu.',
      limitExhausted: 'Limit jest już wyczerpany — każdy kolejny kilometr to przekroczenie.',
      fitHint: 'Aby zmieścić się w limicie, do końca umowy ({days} dni) można jeździć średnio do {km} km miesięcznie.',
    },
    timeline: {
      title: 'Oś czasu umowy',
      description: 'Minęło {percent}% okresu umowy; rocznice startu dzielą ją na lata rozliczeniowe.',
      aria: 'Umowa od {start} do {end}, minęło {percent}% okresu',
      today: 'Dziś',
      start: 'Start umowy',
      end: 'Koniec umowy',
      daysLeft: 'za {days} dni',
      ended: 'umowa zakończona',
      years: 'Lata umowy',
      year: 'Rok {n}',
      current: 'bieżący',
      annualLimit: 'Limit roczny {km}',
    },
  },
  settingsUi: {
    thresholds: {
      title: 'Progi ostrzegawcze limitu km',
      description: 'Kiedy umowa dostaje ostrzeżenie. Dotyczy nowych umów — istniejące zachowują własne progi.',
      previewTitle: 'Podgląd działania progów',
      exampleLimit: 'Przykładowy limit umowy (km)',
      exampleHint: 'Tylko do podglądu — nie jest zapisywany.',
      zoneOk: 'W normie',
      zoneWarn1: 'Ostrzeżenie',
      zoneWarn2: 'Wysokie ryzyko',
      zoneOver: 'Przekroczony',
      atKm: 'od {km}',
      invalid: 'Progi muszą być trzema rosnącymi liczbami całkowitymi 1–100 — podgląd pokazuje ostatnią poprawną wartość.',
      aria: 'Strefy limitu: norma do {t1}%, ostrzeżenie od {t1}%, wysokie ryzyko od {t2}%, przekroczenie od {t3}%',
    },
    forecast: {
      title: 'Minimum danych do prognozy',
      description: 'Poniżej tych wartości prognoza leasingu jest oznaczana jako orientacyjna.',
      preview: 'Prognoza dla umowy jest orientacyjna, dopóki trwa krócej niż {days} dni lub przejechano mniej niż {km} km.',
    },
    logbooks: {
      title: 'Karty drogowe',
      description: 'Limity służą do automatycznych flag na kartach (przebieg ponad plan lub ponad dzienne maksimum).',
      preview: 'Przykład: przy planie {plan} km flaga pojawi się poza zakresem {lo}–{hi} km; karta dłuższa niż {max} km dostaje flagę zawsze.',
    },
    forms: { title: 'Formularze kierowców', description: 'Co kierowca musi wypełnić przy trasie i tankowaniu.' },
    defect: { title: 'Poważna usterka' },
    save: { title: 'Niezapisane zmiany', hint: 'Zmiany wejdą w życie po zapisaniu.' },
  },
};

export const fleetLeasesUiEn: Shape<typeof fleetLeasesUiPl> = {
  leasesUi: {
    rateUnknown: 'rate unknown',
    hero: { limit: 'Limit', rate: 'Rate', rateNone: 'unknown', ofLimit: 'of limit' },
    kpi: {
      exceeded: 'Exceeded by',
      avgHint: 'over {days} days of the contract',
      costHint: 'at a rate of {rate}',
    },
    card: {
      forecast: 'Forecast: {percent}% of limit',
      cost: 'overage cost {cost}',
      daysLeft: '{days} days left',
    },
    summary: {
      active: 'Active leases',
      activeHint: 'All active ones, regardless of filters',
      truncated: 'First {n} active leases',
      atRisk: 'At risk',
      atRiskHint: 'Warning or forecast above the limit',
      exceeded: 'Limit exceeded',
      exceededHint: 'Driven more than the contract limit',
      cost: 'Forecast overage cost',
      costHint: 'Sum only for leases with a known rate',
      costNoRate: 'Leases without a rate not included: {n}',
      costNone: 'No forecast overages with a computable cost',
    },
    chart: {
      title: 'Mileage vs limit path',
      description: 'Cumulative kilometres from odometer readings, the even limit path and the forecast to contract end.',
      aria: 'Cumulative lease mileage chart: {driven} driven of a {limit} limit, forecast at contract end: {forecast}',
      noForecastShort: 'none',
      limitLabel: 'Limit {km}',
      today: 'Today',
      pathAt: 'Limit path: {km}',
      ahead: 'Ahead of path by {km}',
      behind: 'Behind path by {km}',
      legendActual: 'Distance driven',
      legendPath: 'Limit path (even use)',
      legendForecast: 'Forecast',
      legendForecastIndicative: 'Indicative forecast',
      legendZone: 'Overage zone',
      indicativeNote: 'The forecast is indicative — too little data (short period or low mileage).',
      empty: 'Not enough odometer readings to draw the chart',
      emptyHint: 'The chart appears after at least one odometer reading within the contract period.',
      tableToggle: 'Show data as a table',
      colDate: 'Date',
      colKm: 'Driven',
      colPercent: '% of limit',
    },
    sim: {
      title: 'What-if simulator',
      description: 'Change the average driving pace and see the predicted overage. It is an estimate, not a promise.',
      pace: 'Average until contract end',
      perMonth: 'km / month',
      useCurrent: 'Current pace ({km})',
      useFit: 'Fits the limit ({km})',
      resultTitle: 'Predicted mileage at contract end',
      overage: 'Overage',
      ended: 'The contract has ended — a simulation no longer makes sense.',
      limitExhausted: 'The limit is already used up — every further kilometre is overage.',
      fitHint: 'To stay within the limit until the contract ends ({days} days) you can drive up to {km} km per month on average.',
    },
    timeline: {
      title: 'Contract timeline',
      description: '{percent}% of the contract period has passed; start anniversaries split it into contract years.',
      aria: 'Contract from {start} to {end}, {percent}% of the period has passed',
      today: 'Today',
      start: 'Contract start',
      end: 'Contract end',
      daysLeft: 'in {days} days',
      ended: 'contract ended',
      years: 'Contract years',
      year: 'Year {n}',
      current: 'current',
      annualLimit: 'Annual limit {km}',
    },
  },
  settingsUi: {
    thresholds: {
      title: 'Mileage limit warning thresholds',
      description: 'When a lease gets a warning. Applies to new leases — existing ones keep their own thresholds.',
      previewTitle: 'How the thresholds work',
      exampleLimit: 'Example contract limit (km)',
      exampleHint: 'Preview only — not saved.',
      zoneOk: 'Normal',
      zoneWarn1: 'Warning',
      zoneWarn2: 'High risk',
      zoneOver: 'Exceeded',
      atKm: 'from {km}',
      invalid: 'Thresholds must be three ascending whole numbers 1–100 — the preview shows the last valid value.',
      aria: 'Limit zones: normal up to {t1}%, warning from {t1}%, high risk from {t2}%, exceeded from {t3}%',
    },
    forecast: {
      title: 'Minimum data for the forecast',
      description: 'Below these values the lease forecast is marked as indicative.',
      preview: 'A lease forecast is indicative while the contract has run for fewer than {days} days or fewer than {km} km were driven.',
    },
    logbooks: {
      title: 'Logbooks',
      description: 'The limits drive automatic flags on logbooks (mileage above plan or above the daily maximum).',
      preview: 'Example: with a plan of {plan} km a flag appears outside {lo}–{hi} km; a logbook longer than {max} km is always flagged.',
    },
    forms: { title: 'Driver forms', description: 'What the driver must fill in on a route and when fuelling.' },
    defect: { title: 'Serious defect' },
    save: { title: 'Unsaved changes', hint: 'Changes take effect after saving.' },
  },
};
